/* eslint-disable import/extensions */
import React, { useRef, useMemo } from "react";
import classnames from "classnames";
import { Editor as TinyMCEEditor } from "@tinymce/tinymce-react";
import { getCredentialCookies } from "@atd/utils";

// Import TinyMCE assets for bundling
// This ensures they are included in the build and we don't need to serve them from public/
import "tinymce/tinymce";
import "tinymce/models/dom/model";
import "tinymce/themes/silver";
import "tinymce/icons/default";

// Import skin CSS (bundled by style-loader/css-loader)
import "tinymce/skins/ui/oxide/skin.min.css";

// Import Plugins
import "tinymce/plugins/advlist";
import "tinymce/plugins/autolink";
import "tinymce/plugins/lists";
import "tinymce/plugins/link";
import "tinymce/plugins/image";
import "tinymce/plugins/charmap";
import "tinymce/plugins/preview";
import "tinymce/plugins/anchor";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/visualblocks";
import "tinymce/plugins/code";
import "tinymce/plugins/fullscreen";
import "tinymce/plugins/insertdatetime";
import "tinymce/plugins/media";
import "tinymce/plugins/table";
import "tinymce/plugins/wordcount";

function Editor(props) {
  const editorRef = useRef(null);
  const { token } = getCredentialCookies();

  const {
    disabled,
    requestId,
    config = {},
    hasCustomBottomToolbar,
    placeholder,
    className,
    model,
    onModelChange,
  } = props;

  const classes = classnames("atd-tinymce-editor", className, {
    "atd-tinymce-editor--hasCustomBottomToolbar": hasCustomBottomToolbar,
  });

  const handleImageUpload = (blobInfo, progress) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.withCredentials = false;
      xhr.open("POST", `${process.env.REACT_APP_API_URL}/froala_attach`);

      xhr.setRequestHeader("Authorization", `Bearer ${token}`);

      xhr.upload.onprogress = (e) => {
        progress((e.loaded / e.total) * 100);
      };

      xhr.onload = () => {
        if (xhr.status === 403) {
          reject({ message: "HTTP Error: " + xhr.status, remove: true });
          return;
        }

        if (xhr.status < 200 || xhr.status >= 300) {
          reject("HTTP Error: " + xhr.status);
          return;
        }

        let json;
        try {
          json = JSON.parse(xhr.responseText);
        } catch (e) {
          reject("Invalid JSON: " + xhr.responseText);
          return;
        }

        if (!json || typeof json.link !== "string") {
          reject("Invalid JSON: " + xhr.responseText);
          return;
        }

        resolve(json.link);
      };

      xhr.onerror = () => {
        reject(
          "Image upload failed due to a XHR Transport error. Code: " +
            xhr.status,
        );
      };

      const formData = new FormData();
      formData.append("attach", blobInfo.blob(), blobInfo.filename());
      if (requestId) {
        formData.append("request_id", requestId);
      }

      xhr.send(formData);
    });

  const editorConfig = useMemo(
    () => ({
      height: 300,
      menubar: false,
      license_key: "gpl",
      // Important: Disable skin and content css loading from URLs
      // because we are bundling them via imports.
      skin: false,
      content_css: false,
      placeholder: placeholder || config.placeholderText,
      plugins: [
        "advlist",
        "autolink",
        "lists",
        "link",
        // "image",
        "charmap",
        "preview",
        "anchor",
        "searchreplace",
        "visualblocks",
        "code",
        "fullscreen",
        "insertdatetime",
        "media",
        "table",
        "wordcount",
      ],
      toolbar:
        "bold italic underline subscript superscript | bullist numlist | link image | table | removeformat | code",
      // Add basic styling for the iframe content since we disabled content_css
      content_style:
        "body { font-family:Helvetica,Arial,sans-serif; font-size:14px } " +
        // Add minimal reset/normalization if needed, or rely on browser defaults
        "img { max-width: 100%; height: auto; }",
      images_upload_handler: handleImageUpload,
      statusbar: !hasCustomBottomToolbar,
      ...config,
    }),
    [placeholder, config, hasCustomBottomToolbar, requestId, token],
  );

  return (
    <div className={classes}>
      <style>{`
        .tox-tinymce {
            z-index: 0;
        }
        .tox-statusbar {
          display: none !important;
        }
      `}</style>
      <TinyMCEEditor
        onInit={(evt, editor) => (editorRef.current = editor)}
        disabled={disabled}
        value={model}
        onEditorChange={onModelChange}
        init={editorConfig}
      />
    </div>
  );
}

export default Editor;
